/**
 * STYLING: Plain Tailwind ONLY!
 * Text Tools Hub - Landing page showing all 11 tools as cards.
 * Plus "Meine Vorlagen" section on top with workspace-owned templates.
 */

import { useQuery } from '@tanstack/react-query';
import * as Icons from 'lucide-react';
import { Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  fetchTextTools,
  fetchAllWorkspaceTextTemplates,
  type TextTemplate,
} from './textGenerationApi';
import { TEXT_TOOLS, TIER_COLORS, TIER_LABEL_KEYS, type TextToolDef } from './textToolsConfig';

export function TextToolsHub() {
  const { t } = useTranslation();
  const { data: apiTools } = useQuery({
    queryKey: ['text-tools'],
    queryFn: fetchTextTools,
    staleTime: 5 * 60 * 1000,
  });

  const { data: templates } = useQuery({
    queryKey: ['text-templates', 'all'],
    queryFn: fetchAllWorkspaceTextTemplates,
    staleTime: 60 * 1000,
  });

  const tools = TEXT_TOOLS.map((td) => {
    const api = apiTools?.find((a) => a.code === td.code);
    return { ...td, baseCredits: api?.base_credits ?? td.baseCredits, multipliers: api?.model_multipliers ?? {} };
  });

  const tiers = [1, 2, 3] as const;
  const hasTemplates = (templates?.length ?? 0) > 0;

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Text Tools</h1>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          {t('textTools.hubDescription', '11 spezialisierte KI-Text-Tools mit Model-Multiplikator (Flash 1x / Pro 2.5x)')}
        </p>
      </div>

      {hasTemplates && (
        <div id="text-tools-hub-my-templates" className="space-y-3">
          <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
            {t('textTools.myTemplates.title', 'Meine Vorlagen')}
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 -mt-1">
            {t('textTools.myTemplates.subtitle', 'Workspace-Vorlagen mit eigenen Settings + Prompts')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates!.map((tpl) => (
              <TemplateCard key={tpl.id} template={tpl} />
            ))}
          </div>
        </div>
      )}

      {tiers.map((tier) => {
        const tierTools = tools.filter((tl) => tl.tier === tier);
        if (tierTools.length === 0) return null;
        return (
          <div key={tier} className="space-y-3">
            <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
              {t(TIER_LABEL_KEYS[tier])} Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tierTools.map((tool) => (
                <ToolCard key={tool.code} tool={tool} tier={tier} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ToolCard({ tool, tier }: { tool: TextToolDef & { baseCredits: number }; tier: number }) {
  const { t } = useTranslation();
  const IconComponent = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[tool.icon] || Icons.FileText;
  const name = tool.nameKey ? t(tool.nameKey, tool.name) : tool.name;
  const description = tool.descriptionKey ? t(tool.descriptionKey, tool.description) : tool.description;

  return (
    <Link
      to={`/v2/tools/text/${tool.code}`}
      className="block p-4 border border-neutral-200 dark:border-neutral-700 rounded-xl hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all bg-white dark:bg-neutral-800"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-neutral-100 dark:bg-neutral-700">
          <IconComponent className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{name}</h3>
            <span className={`text-2xs font-medium px-1.5 py-0.5 rounded-full ${TIER_COLORS[tier]}`}>
              {t(TIER_LABEL_KEYS[tier])}
            </span>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-2">{description}</p>
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mt-2">
            {t('textTools.fromCredits', { count: tool.baseCredits, defaultValue: `ab ${tool.baseCredits} Credits` })}
          </p>
        </div>
      </div>
    </Link>
  );
}

function TemplateCard({ template }: { template: TextTemplate }) {
  const { t } = useTranslation();
  const subtitle =
    typeof template.user_prompt === 'string' && template.user_prompt.trim().length > 0
      ? template.user_prompt
      : t('textTools.myTemplates.cardFallback', 'Workspace-Vorlage');

  return (
    <Link
      id={`text-tools-hub-template-card-${template.id}`}
      to={`/v2/tools/text/vorlage/${template.id}`}
      className="block p-4 border border-amber-200 dark:border-amber-800/60 rounded-xl hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-md transition-all bg-amber-50/40 dark:bg-amber-950/20"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40">
          <Bookmark className="h-5 w-5 text-amber-700 dark:text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{template.name}</h3>
            <span className="text-2xs font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              {t('textTools.myTemplates.badge', 'Vorlage')}
            </span>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-2">{subtitle}</p>
          {template.tool_code && (
            <p className="text-2xs text-neutral-400 dark:text-neutral-500 mt-2 font-mono truncate">
              {template.tool_code}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
